package com.essntl.features.contact.data.repository

import com.essntl.core.supabase.getAll
import com.essntl.core.supabase.getById
import com.essntl.core.supabase.insert
import com.essntl.core.supabase.update
import com.essntl.core.utils.repository.Repository
import com.essntl.features.contact.data.supabase.ContactDto
import com.essntl.features.contact.data.supabase.ContactSupabaseDataSource
import com.essntl.features.contact.data.supabase.GetContactDto
import com.essntl.features.contact.data.supabase.toRequestMap
import com.essntl.features.contact.domain.model.ContactModel
import com.essntl.features.contact.domain.model.GetContactModel
import com.essntl.features.contact.domain.repository.ContactRepository
import com.essntl.features.organization.data.supabase.toDto
import com.essntl.features.organization.domain.model.OrganizationModel
import io.github.jan.supabase.SupabaseClient
import io.github.jan.supabase.functions.functions
import io.ktor.client.call.body
import org.koin.core.annotation.Single

@Single(binds = [ContactRepository::class])
internal class ContactRepositoryImpl(
    private val supabase: SupabaseClient,
    private val contactDataSource: ContactSupabaseDataSource,
) : Repository(), ContactRepository {

    override suspend fun getContactList(
        type: OrganizationModel.Type,
        searchText: String,
    ): Result<List<GetContactModel>> =
        runCatching {
            supabase.functions.invoke(
                function = "get-contacts?type=${type.toDto()}&searchTerm=$searchText",
            ).body<List<GetContactDto>>()
                .map { dto -> dto.toModel() }
        }

    override suspend fun getByOrganizationId(id: String): Result<List<ContactModel>> =
        runCatching {
            contactDataSource
                .getAll(
                    columns = ContactDto.getColumns(),
                ) {
                    filter {
                        eq("organization_id", id)
                    }
                }
                .map { it.toModel() }
        }

    override suspend fun getOrganizationContacts(
        organizationId: String,
        searchText: String,
    ): Result<List<GetContactModel>> =
        runCatching {
            contactDataSource
                .getAll(
                    columns = ContactDto.getColumns(),
                ) {
                    filter {
                        eq("organization_id", organizationId)
                        and {
                            or {
                                ilike("first_name", "%${searchText}%")
                                ilike("last_name", "%${searchText}%")
                            }
                        }
                    }
                }
                .map {
                    GetContactModel(
                        id = it.id,
                        name = it.firstName.orEmpty() + " " + it.lastName.orEmpty() + it.phoneNumber?.let { p -> " - $p" }.orEmpty(),
                    )
                }
        }

    override suspend fun getContact(id: String): Result<ContactModel> =
        runCatching {
            contactDataSource
                .getById(
                    id = id,
                    columns = ContactDto.getColumns(),
                ).toModel()
        }

    override suspend fun update(contact: ContactModel): Result<ContactModel> =
        runCatching {
            contactDataSource
                .update(
                    id = contact.id,
                    requestMap = contact.toRequestMap(),
                    columns = ContactDto.getColumns(),
                ).toModel()
        }

    override suspend fun insert(contact: ContactModel): Result<ContactModel> =
        runCatching {
            contactDataSource
                .insert(
                    requestMap = contact.toRequestMap(),
                    columns = ContactDto.getColumns(),
                ).toModel()
        }
}
